import { Link, useLocation } from 'react-router-dom';
import { Icon } from './Icon';

/**
 * A placeholder that says which thing is coming and what it will be.
 *
 * The generic version said "Coming Soon" and nothing else, which teaches a
 * reader that the navigation lies. See requirements/PLANNED_FEATURES.md — the
 * copy here is the short version of what is written down there.
 */

interface Plan {
  eyebrow: string;
  title: string;
  blurb: string;
  points: { icon: 'user' | 'clock' | 'check' | 'file-text' | 'book-open' | 'sparkles'; text: string }[];
  cta: { label: string; to: string };
}

const PLANS: Record<string, Plan> = {
  '/projects': {
    eyebrow: 'Projects',
    title: 'Live workshops, starting with the kids track',
    blurb:
      "A small group, one hour, one thing built start to finish — with me there while it happens. Reading gets you a long way, but a ten-year-old learns fastest when someone says “try it now” and then looks at what happened.",
    points: [
      { icon: 'user', text: 'Small groups, so everyone gets looked at' },
      { icon: 'clock', text: 'One session, one finished thing — not a course' },
      { icon: 'file-text', text: 'Starter files before, finished files after' },
      { icon: 'check', text: 'Written up afterwards, so the work outlives the hour' },
    ],
    cta: { label: 'Read the kids track meanwhile', to: '/blogs?category=kids-programming' },
  },
  '/tutorials': {
    eyebrow: 'Tutorials',
    title: 'The short answers, separate from the long ones',
    blurb:
      'A course is ten chapters and several hours. A tutorial is one problem and twenty minutes — set this up, fix that error, read this output. The things you search for at the moment you are stuck, rather than sit down to study.',
    points: [
      { icon: 'clock', text: 'Twenty minutes, one outcome named in the title' },
      { icon: 'file-text', text: 'Exact commands and finished files, made to be copied' },
      { icon: 'sparkles', text: 'Version-pinned and dated, so you know if it still holds' },
      { icon: 'book-open', text: 'Linked from the chapters that assume them' },
    ],
    cta: { label: 'Browse the courses meanwhile', to: '/series' },
  },
};

const FALLBACK: Plan = {
  eyebrow: 'Coming soon',
  title: 'Being written',
  blurb: 'This part of jslipi is not finished yet. Everything else is free to read in the meantime.',
  points: [],
  cta: { label: 'Browse everything', to: '/blogs' },
};

export const ComingSoonPage = () => {
  const { pathname } = useLocation();
  const plan = PLANS[pathname] ?? FALLBACK;

  return (
    <div className="min-h-[70vh] bg-surface text-on-surface">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 sm:pt-28 pb-20">
        <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-secondary mb-4">
          {plan.eyebrow} · in the works
        </p>

        <h1 className="font-headline text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] text-foreground mb-5">
          {plan.title}
        </h1>

        <p className="text-lg sm:text-xl font-body leading-relaxed text-on-surface-variant mb-9">
          {plan.blurb}
        </p>

        {plan.points.length > 0 && (
          <ul className="space-y-3.5 mb-10">
            {plan.points.map((point) => (
              <li key={point.text} className="flex items-start gap-3">
                <Icon name={point.icon} size={18} className="mt-0.5 shrink-0 text-primary" />
                <span className="text-[15px] sm:text-base leading-relaxed text-on-surface-variant">
                  {point.text}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-4">
          <Link
            to={plan.cta.to}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-bold text-primary-foreground hover:bg-primary-container transition-colors"
          >
            {plan.cta.label}
            <Icon name="arrow-right" size={18} />
          </Link>

          {/* No date promised. A missed one is worse than none. */}
          <Link
            to="/about#contact"
            className="text-sm font-semibold text-primary hover:underline"
          >
            Tell me what you want from it
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage;
